import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { UserService } from '../../services/user.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { LogoComponent } from "../../components/logo/logo.component";
import { UserProfileAvatarComponent } from "../../components/user-profile-avatar/user-profile-avatar.component";
import { TokenService } from '../../services/token.service';

/**
 * AddUserComponent
 * * Provides a form interface for administrators to manually create a new user/recipient.
 * Responsibilities:
 * - Collects user details (Name, Email, Phone, Role, etc.).
 * - Validates inputs (Email regex, Phone format).
 * - Displays current user statistics in the header.
 * - Submits payload to the UserService to create the entity.
 */
@Component({
  selector: 'app-add-user',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, LogoComponent, UserProfileAvatarComponent],
  templateUrl: './add-user.component.html',
  styles: [`
    main::-webkit-scrollbar { width: 6px; }
    main::-webkit-scrollbar-track { background: #f1f1f1; }
    main::-webkit-scrollbar-thumb { background: #d4d4d8; border-radius: 10px; }
  `]
})
export class AddUserComponent implements OnInit {
  /** Service to handle user creation API calls */
  private userService = inject(UserService);
  /** Service to show success/error notifications */
  private notification = inject(NzNotificationService);
  /** Router for navigation */
  private router = inject(Router);
  /** Token service for logout */
  private tokenService = inject(TokenService);

  // --- Stats State (Placeholder/Calculated) ---
  totalUsers = 0;
  standardUsers = 0;
  adminUsers = 3;
  superAdmins = 4;

  // --- Form Model ---
  /**
   * Data model bound to the form inputs.
   */
  user = {
    firstName: '',
    lastName: '',
    email: '',
    phoneCode: '+1',
    phoneNumber: '',
    employeeId: '',
    organization: '',
    plan: '',
    role: '',
    notes: ''
  };

  /** Available provider organizations (UI logic placeholder) */
  providerOrgs = [
    { label: 'HealthPlus Providers', checked: false },
    { label: 'WellCare Medical Group', checked: false },
    { label: 'CareFirst Network', checked: false },
    { label: 'United Healthcare Network', checked: false }
  ];

  /** Available permissions for the new user */
  permissions = [
    { label: 'Dashboard Access', checked: false },
    { label: 'View Recipients', checked: false },
    { label: 'Manage Recipients', checked: false },
    { label: 'Send Messages', checked: false },
    { label: 'View Reports', checked: false },
    { label: 'Manage Users', checked: false },
    { label: 'System Configuration', checked: false },
    { label: 'Organization Management', checked: false },
    { label: 'Multi-Organization Access', checked: false }
  ];

  /**
   * Lifecycle hook: Init component.
   */
  ngOnInit() {
    this.fetchUserStats();
  }

  /**
   * Fetches total user count to update the dashboard stats shown on this page.
   * Simple calculation logic for demonstration.
   */
  fetchUserStats() {
    this.userService.getUsers().subscribe({
      next: (users: any[]) => {
        this.totalUsers = users.length;
        // Basic deduction for display purposes
        this.standardUsers = Math.max(0, this.totalUsers - 7);
      },
      error: (err) => console.error('Failed to load user stats', err)
    });
  }

  /**
   * Logs out the current user.
   */
  logout() {
    this.tokenService.clearToken();
    this.router.navigate(['/login']);
  }

  /**
   * Validates form inputs and submits the create request to the API.
   * On success, redirects back to the Intake Dashboard.
   */
  saveUser() {
    // 1. Email Validation
    if (!this.isValidEmail(this.user.email)) {
      this.notification.error('Validation Error', 'Please enter a valid email address.');
      return;
    }

    // 2. Phone Validation
    const cleanPhone = this.stripPhone(this.user.phoneNumber);
    if (cleanPhone.length !== 10) {
      this.notification.error('Validation Error', 'Phone number must have exactly 10 digits.');
      return;
    }

    // 3. Construct Payload
    const payload = {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      email: this.user.email,
      phoneNumber: `${this.user.phoneCode}${cleanPhone}`,
      usernameType: 'phone-number',
      metadata: {
        employeeId: this.user.employeeId,
        organization: this.user.organization,
        plan: this.user.plan,
        role: this.user.role,
        notes: this.user.notes,
        // Map checked permissions to string array
        permissions: this.permissions.filter(p => p.checked).map(p => p.label)
      }
    };

    // 4. API Call
    this.userService.createUser(payload).subscribe({
      next: () => {
        this.notification.success('Success', 'User created successfully.');
        this.router.navigate(['/intake']);
      },
      error: (err) => {
        // Log error and show notification
        console.error(err);
        const errorMessage = err.error?.error?.message || 'An unexpected error occurred.';
        this.notification.error('Creation Failed', errorMessage);
      }
    });
  }

  /**
   * Cancels the operation and navigates back to the dashboard.
   */
  cancel() {
    this.router.navigate(['/intake']);
  }

  /**
   * Helper function to validate email format via Regex.
   * * @param email - The email string to check.
   * @returns True if valid.
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  /**
   * Helper function to strip non-numeric characters from a phone string.
   * * @param phone - Raw phone input.
   * @returns numeric string.
   */
  private stripPhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }
}